import React, { useEffect, useState } from "react";
import { Save, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { TextField, Section, StringList, PageHeader } from "./AdminUI";
import ImageUploader from "./ImageUploader";

const AboutAdmin = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const about = await api.getAbout();
        setData(about || {});
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const updateProcess = (idx, field, v) => {
    const list = [...(data.process || [])];
    list[idx] = { ...list[idx], [field]: v };
    set("process", list);
  };
  const removeProcess = (idx) => set("process", (data.process || []).filter((_, i) => i !== idx));
  const addProcess = () => set("process", [...(data.process || []), { step: "", title: "", text: "" }]);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const saved = await api.saveAbout(data);
      setData(saved);
      toast({ title: "Saved", description: "About section updated." });
    } catch (err) {
      toast({ title: "Couldn't save", description: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="py-20 text-center">
        <Loader2 className="w-6 h-6 animate-spin inline-block text-[color:var(--muted)]" />
      </div>
    );
  }

  return (
    <form onSubmit={onSave} className="space-y-8">
      <PageHeader
        label="About"
        title="Your story & process"
        action={
          <button type="submit" disabled={saving} className="mk-btn mk-btn-primary disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
          </button>
        }
      />

      <Section title="Heading">
        <TextField label="Section label" value={data.section_label} onChange={(v) => set("section_label", v)} placeholder="About" />
        <div className="grid md:grid-cols-3 gap-5">
          <TextField label="Prefix" value={data.heading_pre} onChange={(v) => set("heading_pre", v)} />
          <TextField label="Highlighted word" value={data.heading_em} onChange={(v) => set("heading_em", v)} />
          <TextField label="Suffix" value={data.heading_post} onChange={(v) => set("heading_post", v)} />
        </div>
      </Section>

      <Section title="Photo">
        <ImageUploader
          value={data.designer_image}
          onChange={(v) => set("designer_image", v)}
          label="Designer photo"
          aspect="aspect-[4/5]"
        />
        <div className="mt-4">
          <div className="text-xs font-medium uppercase tracking-wider text-[color:var(--muted)] mb-2">
            Photo position (relative to the text)
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => set("image_position", "left")}
              data-testid="about-image-left"
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                (data.image_position || "left") === "left"
                  ? "bg-[color:var(--violet)] text-white border-[color:var(--violet)]"
                  : "border-black/15 text-[color:var(--ink)]"
              }`}
            >
              Image on left
            </button>
            <button
              type="button"
              onClick={() => set("image_position", "right")}
              data-testid="about-image-right"
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                data.image_position === "right"
                  ? "bg-[color:var(--violet)] text-white border-[color:var(--violet)]"
                  : "border-black/15 text-[color:var(--ink)]"
              }`}
            >
              Image on right
            </button>
          </div>
        </div>
      </Section>

      <Section title="Bio paragraphs" hint="Each item is one paragraph">
        <StringList items={data.paragraphs || []} onChange={(v) => set("paragraphs", v)} placeholder="Write a paragraph…" />
      </Section>

      <Section title="Quote">
        <TextField textarea rows={3} value={data.quote} onChange={(v) => set("quote", v)} />
      </Section>

      <Section title="Process steps">
        <div className="space-y-4">
          {(data.process || []).map((p, i) => (
            <div key={i} className="p-4 rounded-xl border border-black/10">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-wider text-[color:var(--muted)]">Step {i + 1}</span>
                <button type="button" onClick={() => removeProcess(i)} className="text-xs text-[color:var(--coral)] hover:underline">
                  Remove
                </button>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <TextField label="Step number" value={p.step} onChange={(v) => updateProcess(i, "step", v)} placeholder="01" />
                <TextField label="Title" value={p.title} onChange={(v) => updateProcess(i, "title", v)} placeholder="Discover" />
              </div>
              <div className="mt-3">
                <TextField label="Description" textarea rows={2} value={p.text} onChange={(v) => updateProcess(i, "text", v)} />
              </div>
            </div>
          ))}
        </div>
        <button type="button" onClick={addProcess} className="text-sm text-[color:var(--coral)] hover:underline">
          + Add another step
        </button>
      </Section>

      <div className="flex justify-end">
        <button type="submit" disabled={saving} className="mk-btn mk-btn-primary disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </form>
  );
};

export default AboutAdmin;
